import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import VerticalLayout from "../../../../../lib/html5-software-library/src/ranj/components/layouts/VerticalLayout";
import ScrollPane from "../../../../../lib/html5-software-library/src/ranj/components/scrolling/ScrollPane";
import Rectangle from "../../../../../lib/html5-software-library/src/ranj/geom/Rectangle";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import TrackString from "../../../analytics/TrackString";
import MediatorManager from "../../../core/application/MediatorManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import { GameMessageSender } from "../../conversation/model/GameConversationMessage";
import ConversationInputPanelMediator from "../../conversation/view/ConversationInputPanelMediator";
import DebugModel from "../../debug/model/DebugModel";
import PlayerModel from "../../player/model/PlayerModel";
import SessionModel from "../../session/model/SessionModel";
import DisplayObjectUtil from "../../util/DisplayObjectUtil";
import PromiseUtil from "../../util/PromiseUtil";
import ConversationReflectionPlayer from "../controller/ConversationReflectionPlayer";
import ConversationReflectionAllowEndEvent from "../event/ConversationReflectionAllowEndEvent";
import ConversationReflectionEndedEvent from "../event/ConversationReflectionEndedEvent";
import ConversationReflectionEvent from "../event/ConversationReflectionEvent";
import ConversationReflectionStartedEvent from "../event/ConversationReflectionStartedEvent";
import NewConversationReflectionMessagesEvent from "../event/NewConversationReflectionMessagesEvent";
import ConversationReflectionMessage from "../model/ConversationReflectionMessage";

export default class ConversationReflectionScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {AssetLibrary} assetLibrary
     * @param {PlayerModel} playerModel
     * @param {RevisableLanguagePack} languagePack 
     * @param {MediatorManager} mediatorManager 
     * @param {SessionModel} sessionModel 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, assetLibrary, playerModel, languagePack, mediatorManager, sessionModel, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._assetLibrary = assetLibrary;
        this._playerModel = playerModel;
        this._languagePack = languagePack;
        this._mediatorManager = mediatorManager;
        this._sessionModel = sessionModel;
        this._soundManager = soundManager

        /** @type {string} */
        this._backgroundImageId = this.getCustomData().backgroundImageId;
        /** @type {ConversationReflectionPlayer} */
        this._conversationReflectionPlayer = this.getCustomData().conversationReflectionPlayer;
        /** @type {boolean} */
        this._hideCoachUntilFirstMessage = this.getCustomData().hideCoachUntilFirstMessage;

        this._allowEnd = false;
        this._hasEnded = false;
        /** @type {ConversationReflectionMessage[]} */
        this._messagesQueue = [];
        this._untilNextMessageShown = Promise.resolve();
    }

    initialize() {
        super.initialize();

        this.preventClickThrough();

        if (this._hideCoachUntilFirstMessage) {
            this._hideCoachInstant();
        } else {
            this._showCoachInstant();
        }

        this._initializeBackground();
        this._initializeLayout();

        const halvedDragButtonHeight = this._target.reflectionPanel.scrollBar.dragButton.nominalBounds.height * .5;
        const reflectionScrollPaneBounds = this._target.reflectionPanel.scrollPane.nominalBounds;
        const viewport = new Rectangle(0, 0, reflectionScrollPaneBounds.width, reflectionScrollPaneBounds.height);
        this._reflectionScrollPane = new ScrollPane(viewport, null, this._target.reflectionPanel.scrollBar, this._panelContentLayoutContainer, null, true, true, halvedDragButtonHeight);

        this._target.titleTextField.text = this._languagePack.getText('conversationReflection.title');
        this._target.sessionIdTextField.text = `Session: ${this._sessionModel.shortSessionId}`;

        this._reflectionPanelContent = this._target.reflectionPanel.scrollPane;
        this._reflectionPanelContent.outputTextField.text = "";
        this._reflectionPanelContent.divTextField.initialText = this._reflectionPanelContent.divTextField.text;
        this._reflectionPanelContent.divTextField.text = "";
        this._reflectionPanelContent.aiCoachTextField.text = "";

        this._readyPanel = this._target.reflectionPanel.readyPanel;
        this._readyPanel.promptTextField.text = this._languagePack.getText('conversationReflection.readyPrompt');
        this._readyPanel.divTextField.initialText = this._readyPanel.divTextField.text;
        this._readyPanel.visible = false; // Don't allow learners to directly interact with the panel and finish the reflection.

        this._reflectionScrollPane.update();

        /** @type {ConversationInputPanelMediator} */
        this._inputPanel = this._mediatorManager.createMediator(this._target.inputPanel, SkinKeyRegistry.CONVERSATION_INPUT_PANEL_COMPONENT);
        this._inputPanel.eventDispatcher.addEventListener(ConversationInputPanelMediator.PLAYER_INPUT_EVENT, this._submitPlayerInput.bind(this));
        this._inputPanel.hideInstant();

        this._finishButton = this.createBracketButton(this._target.reflectionPanel.readyPanel.finishButton, this._handleFinishReflection.bind(this), this._languagePack.getText(`conversationReflection.finishButtonLabel`), SoundType.BRACKET_BUTTON_CLICK);
        this._finishButton.setVisible(false);

        this.registerEventListener(ConversationReflectionStartedEvent.PERFORM, this._handleConversationStarted.bind(this));
        this.registerEventListener(ConversationReflectionEndedEvent.PERFORM, this._handleConversationEnded.bind(this));
        this.registerEventListener(ConversationReflectionAllowEndEvent.PERFORM, this._handleConversationAllowEnd.bind(this));
        this.registerEventListener(NewConversationReflectionMessagesEvent.TYPE, this._handleNewMessages.bind(this));
    }

    destroy() {
        super.destroy();

        this._inputPanel.destroy();
    }

    _submitPlayerInput(evt) {
        const currentText = evt.playerInput;
        if (currentText.length === 0) {
            return;
        }

        this._inputPanel.setEnabled(false);

        // Track analytics.
        this.trackEvent(TrackString.EVENT_REFLECTION_PLAYER_TURN, TrackString.createReflectionPlayerTurnParams({
            reflectionId: this._conversationReflectionPlayer.conversation.id,
            turnIndex: this._conversationReflectionPlayer.conversation.turns,
            numberOfInputCharacters: currentText.length,
        }));

        // Submit the player input and advance the conversation.
        this._conversationReflectionPlayer.submitPlayerInput(currentText);
    }

    _handleFinishReflection() {
        this._soundManager.stop();

        console.log(`[${ConversationReflectionScreenMediator.name}] Reflection completed.`);
        this.dispatch(ConversationReflectionEvent.COMPLETED);
    }

    _handleConversationStarted(evt) {
        if (!this._coachIsVisible()) {
            this._showCoach();
        }
    }

    _handleConversationEnded(evt) {
        this._inputPanel.setEnabled(false);
        this._inputPanel.hide();

        // Hide the coach.
        this._soundManager.playSound(SoundType.COACH_HIDE);
        createjs.Tween.get(this._target.robbyReflection)
            .to({ alpha: 0, y: this._target.robbyReflection.y + 200, }, 650, createjs.Ease.backIn);

        this._allowEnd = true;
        this._hasEnded = true;
    }

    _handleConversationAllowEnd(evt) {
        this._allowEnd = true;
    }

    async _handleNewMessages(evt) {
        /** @type {NewConversationReflectionMessagesEvent} */
        const data = evt.params.data;
        const messages = data.messages;

        this._messagesQueue.push(...messages);
        this._showNextMessage();
    }

    async _showNextMessage() {
        if (this._messagesQueue.length === 0) {
            return;
        }

        // Wait until the previous message has been shown.
        await this._untilNextMessageShown;
        this._untilNextMessageShown = new Promise(resolve => this._notifyMessageShown = resolve);

        const message = this._messagesQueue.shift();
        await this._showMessage(message);

        // Resolves the promise to show the next message.
        this._notifyMessageShown();
    }

    /**
     * 
     * @param {ConversationReflectionMessage} message 
     */
    async _showMessage(message) {
        switch (message.sender) {
            case GameMessageSender.PLAYER:
                this._clearCoachText();
                this._reflectionScrollPane.update();
                this._reflectionScrollPane.setY(0);
                this._reflectionPanelContent.divTextField.text = "";
                await this._setPlayerText(message.content);

                // Force layout update on end state.
                this._reflectionPanelContent.divTextField.text = this._reflectionPanelContent.divTextField.initialText;
                this._panelContentLayout.repositionItems();

                // const typewriteSoundId = this._soundManager.playSound(SoundType.TYPEWRITER_GENERIC, null, null, 0, 0, -1); // Don't play sound for divider line.
                await this._typeWriteText(this._reflectionPanelContent.divTextField, this._reflectionPanelContent.divTextField.initialText);
                // this._soundManager.stopSound(typewriteSoundId, 50);

                break;
            case GameMessageSender.COACH:
                await this._setCoachText(message.content);

                if (!this._hasEnded) {
                    await this._inputPanel.show();
                    this._inputPanel.setEnabled(true);
                    this._inputPanel.focusInputField();
                }

                if (this._allowEnd) {
                    await this._showReadyPanel();
                }
                break;

            default:
                console.warn(`[${ConversationReflectionScreenMediator.name}] Unknown message sender: ${message.sender}`);
                break;
        }
    }

    _initializeBackground() {
        this._backgroundImageContainer = this._target.bgImageContainer;
        this._backgroundImageContainer.removeAllChildren();

        const backgroundImage = this._assetLibrary.getAsset(this._backgroundImageId);
        if (!backgroundImage) {
            console.warn(`Background image with id "${this._backgroundImageId}" not found.`);
            return;
        }
        const backgroundBitmap = new createjs.Bitmap(backgroundImage);
        DisplayObjectUtil.anchorDisplayObject(backgroundBitmap, Anchor.C);
        this._backgroundImageContainer.addChild(backgroundBitmap);
    }

    _initializeLayout() {
        this._panelContentLayoutContainer = new createjs.Container();
        this._panelContentLayoutContainer.x = this._target.reflectionPanel.scrollPane.outputTextField.x;
        this._panelContentLayoutContainer.y = this._target.reflectionPanel.scrollPane.outputTextField.y;
        this._target.reflectionPanel.scrollPane.addChild(this._panelContentLayoutContainer);

        this._panelContentLayout = new VerticalLayout(this._panelContentLayoutContainer, 20);
        this._panelContentLayout.addItem(this._target.reflectionPanel.scrollPane.outputTextField);
        this._panelContentLayout.addItem(this._target.reflectionPanel.scrollPane.divTextField);
        this._panelContentLayout.addItem(this._target.reflectionPanel.scrollPane.aiCoachTextField);
    }

    async _setPlayerText(text) {
        const prefixedText = `${this._playerModel.getTrimmedPlayerName()}:\n\n${text}`;

        // Force layout update on end state.
        this._reflectionPanelContent.outputTextField.text = prefixedText;
        this._panelContentLayout.repositionItems();

        const typewriteSoundId = this._soundManager.playSound(SoundType.TYPEWRITER_GENERIC, null, null, 0, 0, -1);
        await this._typeWriteText(this._reflectionPanelContent.outputTextField, prefixedText);
        this._soundManager.stopSound(typewriteSoundId, 50);

        this._panelContentLayout.repositionItems();
        this._reflectionScrollPane.update();
    }

    _clearPlayerText() {
        this._reflectionPanelContent.outputTextField.text = "";
    }

    async _setCoachText(text) {
        const prefixedText = `${this._languagePack.getText('coach.name')}:\n\n${text}`;

        // Force layout update on end state.
        this._reflectionPanelContent.aiCoachTextField.text = prefixedText;
        this._panelContentLayout.repositionItems();

        const typewriteSoundId = this._soundManager.playSound(SoundType.TYPEWRITER_COACH, null, null, 0, 0, -1);
        await this._typeWriteText(this._reflectionPanelContent.aiCoachTextField, prefixedText, () => this._onUpdateCoachText());
        this._soundManager.stopSound(typewriteSoundId, 50);

        this._panelContentLayout.repositionItems();
        this._reflectionScrollPane.update();
    }

    _onUpdateCoachText() {
        this._reflectionScrollPane.update();

        const scrollPaneBounds = this._reflectionScrollPane.determineBounds();
        this._reflectionScrollPane.setY(-scrollPaneBounds.height);
    }

    _clearCoachText() {
        this._reflectionPanelContent.aiCoachTextField.text = "";
    }

    async _showReadyPanel() {
        if (this._readyPanel.visible) {
            return; // Already shown.
        }

        this._readyPanel.promptTextField.text = '';
        this._readyPanel.divTextField.text = '';
        this._finishButton.setVisible(false);

        this._readyPanel.visible = true;

        await this._typeWriteText(this._readyPanel.divTextField, this._readyPanel.divTextField.initialText); // NOTE: we don't play sound for the divider line.
        const typewriteSoundId = this._soundManager.playSound(SoundType.TYPEWRITER_COACH, null, null, 0, 0, -1);
        await this._typeWriteText(this._readyPanel.promptTextField, this._languagePack.getText('conversationReflection.readyPrompt'));
        this._soundManager.stopSound(typewriteSoundId, 50);

        await PromiseUtil.delay(150);
        this._finishButton.setVisible(true);
    }

    async _typeWriteText(textField, targetText = "", onUpdate = null) {
        await new Promise(resolve => {
            if (targetText.length === 0) {
                resolve();
                return;
            }

            textField.text = "";

            const timePerCharacter = DebugModel.fasterAnimations ? 0.0001 : 0.015;
            const duration = targetText.length * timePerCharacter;
            TextTypeWriterAnimation.animate(textField, targetText, duration, 0, resolve, null, onUpdate);
        });
    }

    async _showCoach() {
        this._target.robbyReflection.alpha = 0;
        createjs.Tween.get(this._target.robbyReflection)
            .to({ alpha: 1 }, 450, createjs.Ease.linear);

        this._soundManager.playSound(SoundType.COACH_SHOW);
        this._target.robbyReflection.visible = true;
        this._target.robbyReflection.play();
    }

    _showCoachInstant() {
        this._target.robbyReflection.visible = true;
        this._target.robbyReflection.gotoAndStop(this._target.robbyReflection.totalFrames - 1);
    }

    _coachIsVisible() {
        return this._target.robbyReflection.visible;
    }

    _hideCoachInstant() {
        this._target.robbyReflection.visible = false;
    }
}